/**
 * Utility functions for formatting stats data
 */

import { StatRecord } from '@/sharedTypes/Api';

/**
 * Duration unit translations for i18n
 */
export interface DurationUnits {
  hours: string;
  minutes: string;
}

const defaultDurationUnits: DurationUnits = { hours: 'h', minutes: 'm' };

/**
 * Format duration from milliseconds to human-readable format
 * @param ms Duration in milliseconds
 * @param units Optional translated units (default: { hours: 'h', minutes: 'm' })
 * @returns Formatted string like "12h 36m" or "45m"
 */
export function formatDuration(ms: number, units: DurationUnits = defaultDurationUnits): string {
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0) {
    return `${hours}${units.hours} ${minutes}${units.minutes}`;
  }
  return `${minutes}${units.minutes}`;
}

/**
 * Format accuracy from decimal to percentage
 * @param accuracy Accuracy as decimal (0-1)
 * @returns Formatted string like "87%"
 */
export function formatAccuracy(accuracy: number): string {
  return `${Math.round(accuracy * 100)}%`;
}

/**
 * Date label translations for i18n
 */
export interface DateLabels {
  today: string;
  yesterday: string;
}

const defaultDateLabels: DateLabels = { today: 'Today', yesterday: 'Yesterday' };

/**
 * Get relative date label (Today, Yesterday, or formatted date)
 * @param dateStr ISO date string (YYYY-MM-DD)
 * @param labels Optional translated labels (default: { today: 'Today', yesterday: 'Yesterday' })
 * @param locale Optional locale for date formatting (default: 'en-US')
 * @returns Relative label like "Today", "Yesterday", or "Nov 23"
 */
export function getDateLabel(
  dateStr: string,
  labels: DateLabels = defaultDateLabels,
  locale: string = 'en-US'
): string {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const todayStr = today.toISOString().split('T')[0];
  const yesterdayStr = yesterday.toISOString().split('T')[0];

  if (dateStr === todayStr) {
    return labels.today;
  }
  if (dateStr === yesterdayStr) {
    return labels.yesterday;
  }

  return date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Group stats by day for display
 * @param stats Array of stat records
 * @returns Map of date strings to stat records
 */
export function groupByDay(stats: StatRecord[]): Map<string, StatRecord[]> {
  const groups = new Map<string, StatRecord[]>();

  for (const stat of stats) {
    if (!stat.createdAt) continue;

    const date = stat.createdAt.split('T')[0]; // YYYY-MM-DD
    if (!groups.has(date)) {
      groups.set(date, []);
    }
    groups.get(date)!.push(stat);
  }

  return groups;
}

/**
 * Format number with locale-specific thousands separator
 * @param num Number to format
 * @param locale Optional locale for number formatting (default: 'en-US')
 * @returns Formatted string like "15,432" (en-US) or "15 432" (ru-RU)
 */
export function formatNumber(num: number, locale: string = 'en-US'): string {
  return num.toLocaleString(locale);
}

/**
 * Calculate accuracy from correct and total notes
 * @param correctNotes Number of correct notes
 * @param totalNotes Total number of notes
 * @returns Accuracy as decimal (0-1)
 */
export function calculateAccuracy(correctNotes: number, totalNotes: number): number {
  if (totalNotes === 0) return 0;
  return correctNotes / totalNotes;
}
